import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useLazyLoadQuery } from 'react-relay'
import { graphql } from 'relay-runtime'
import { Tooltip } from '@mui/material'

import { ThemeInsight } from '../ThemeInsight/ThemeInsight'
import { ClusteringInsightsQuery } from './__generated__/ClusteringInsightsQuery.graphql'
import { yyyymmdd } from '../../../../display'
import { ChangeEvent, useState } from 'react'
import { KPI } from '../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'
import { useInsightSearchQueries } from '../../hooks/useGlobalSearchQueries'

export const clusteringInsightsQuery = graphql`
  query ClusteringInsightsQuery($input: ListInsightV2Request!) {
    clusteringInsights: listInsightV2(input: $input) {
      data {
        id
        ...ThemeInsight_InsightV2
      }

      metadata {
        dateRange {
          start
          end
        }
      }
    }
  }
`

interface ClusteringInsightsProps {
  brandId: string
}
export const ClusteringInsights = ({ brandId }: ClusteringInsightsProps) => {
  const searchQueries = useInsightSearchQueries()
  const [targetKpi, setTargetKpi] = useState<KPI>('ROAS')

  const data = useLazyLoadQuery<ClusteringInsightsQuery>(
    clusteringInsightsQuery,
    {
      input: {
        pagination: {
          take: 20,
        },
        filter: {
          // TODO: Make this a select dropdown value
          kpi: targetKpi,
          brandId,
          labelTypes: ['THEME'],
          searchQueries,
          includeAllDates: true,
        },
        orderBy: 'LABEL_LIFT_DESC',
      },
    },
    { fetchPolicy: 'store-and-network' }
  )

  const showEmptyState = !data?.clusteringInsights.data.length
  const startDate = data?.clusteringInsights.metadata.dateRange?.start
  const endDate = data?.clusteringInsights.metadata.dateRange?.end

  function handleKpiChange(e: ChangeEvent<HTMLSelectElement>) {
    const val = e.currentTarget.value as KPI

    setTargetKpi(val)
  }

  return (
    <div className="px-8">
      <div className="flex items-center gap-2">
        <h5 className="text-2xl">Themes</h5>

        <Tooltip
          title={
            <div className="p-2">
              <div className="flex items-center gap-2">
                <div className="bg-darkBlue h-4 w-4 rounded-sm" />
                <span>Video</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-lightBlue h-4 w-4 rounded-sm" />
                <span>Image</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-midGray h-4 w-4 rounded-sm" />
                <span>Text</span>
              </div>
            </div>
          }
        >
          <InformationCircleIcon className="h-4 w-4 translate-y-[2px] text-gray-600" />
        </Tooltip>
      </div>
      <p>
        The following themes and label clusters correlate with higher (+) or
        lower (-) performance.
        {startDate && endDate && (
          <>
            <br />
            The performance for these labels is measured between{' '}
            {yyyymmdd(startDate)} and {yyyymmdd(endDate)}.
          </>
        )}
      </p>

      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap">Target KPI:</span>
        <select
          className="block rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 "
          defaultValue={targetKpi}
          onChange={handleKpiChange}
        >
          <option value="ROAS">ROAS</option>
          <option value="PURCHASES_TO_IMPRESSIONS">
            Website Purchases to Impressions
          </option>
          <option value="COST_PER_PURCHASE">Cost per Website Purchase</option>
        </select>
      </div>

      <div className="mt-6 overflow-y-hidden overflow-x-scroll">
        <div className="flex gap-12 py-2">
          {showEmptyState && (
            <div className="text-midGray">
              No theme insights found for this brand/kpi.
            </div>
          )}
          {data.clusteringInsights?.data.map((insight) => (
            <ThemeInsight fragmentKey={insight} key={insight.id} />
          ))}
        </div>
      </div>
    </div>
  )
}
